import { useMemo, useState } from 'react';
import type { ClarifyingQuestion, Task } from '../lib/types';
import { useAnswerQuestions, useCancelTask } from '../api/tasks';

interface Props {
  task: Task;
  projectSlug: string;
}

/**
 * Renders the Comprehender's pending clarifying questions with a radio
 * option list per question PLUS a "Custom answer" text input that always
 * overrides any radio selection. The Comprehender's wording is sometimes
 * imperfect; the custom-text path is the escape hatch for that.
 */
export default function ClarifyingQuestionsPanel({ task, projectSlug }: Props) {
  const questions = (task.clarifying_questions ?? []) as ClarifyingQuestion[];
  const answerMut = useAnswerQuestions(task.id, projectSlug);
  const cancelMut = useCancelTask(task.id, projectSlug);

  // Per-question state: which radio is picked, and any custom-text override.
  const initial = useMemo(() => {
    const state: Record<string, { picked: string; custom: string }> = {};
    for (const q of questions) {
      const recommended = q.options.find((o) => o.recommended);
      state[q.id] = {
        picked: recommended?.value ?? q.options[0]?.value ?? '',
        custom: '',
      };
    }
    return state;
  }, [questions]);
  const [state, setState] = useState(initial);

  if (questions.length === 0) return null;

  const setPicked = (id: string, value: string): void => {
    setState((s) => ({ ...s, [id]: { ...s[id]!, picked: value } }));
  };
  const setCustom = (id: string, value: string): void => {
    setState((s) => ({ ...s, [id]: { ...s[id]!, custom: value } }));
  };

  const handleSubmit = (): void => {
    const answers = questions.map((q) => {
      const s = state[q.id]!;
      const custom = s.custom.trim();
      return custom
        ? { question_id: q.id, answer: custom, custom: true }
        : { question_id: q.id, answer: s.picked };
    });
    answerMut.mutate(answers);
  };

  const handleCancel = (): void => {
    if (
      window.confirm(
        'Cancel this task? The pipeline is paused waiting for your answers; cancelling will mark it as cancelled and not run.',
      )
    ) {
      cancelMut.mutate();
    }
  };

  // Two pause sources reach this panel — render them distinctly so
  // the user knows which kind of decision they're making:
  //   - pending_questions: Comprehender (planning), low-stakes scoping
  //   - pending_questions_in_execution: Jr (mid-execution), BLOCKING
  //     because of a data-loss / broken-build / irreversible / security
  //     risk classified by the Jr severity rule.
  const isExecutionPause = task.status === 'pending_questions_in_execution';
  const headerClass = isExecutionPause
    ? 'sticky top-2 z-10 rounded border border-red-400 bg-red-50 p-4 mb-4 shadow-sm'
    : 'sticky top-2 z-10 rounded border border-orange-300 bg-orange-50 p-4 mb-4 shadow-sm';
  const titleClass = isExecutionPause ? 'text-sm font-semibold text-red-900' : 'text-sm font-semibold text-orange-900';
  const bodyClass = isExecutionPause ? 'text-xs text-red-800 mt-0.5' : 'text-xs text-orange-800 mt-0.5';
  const title = isExecutionPause
    ? '🛑 Mid-execution: blocking question'
    : '❓ Pipeline needs clarification';
  const bodyText = isExecutionPause
    ? `${questions.length} blocking question${questions.length === 1 ? '' : 's'} — Jr stopped mid-step because proceeding without your answer could cause data loss, a broken build, an irreversible side effect, or security exposure. Type your answer to resume execution.`
    : `${questions.length} question${questions.length === 1 ? '' : 's'} — pick an option, or type your own answer to override (useful when the question itself is off-base).`;

  return (
    <div className={headerClass}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className={titleClass}>{title}</h2>
          <p className={bodyClass}>{bodyText}</p>
        </div>
      </div>

      <div className="space-y-4">
        {questions.map((q, i) => (
          <QuestionBlock
            key={q.id}
            index={i + 1}
            question={q}
            picked={state[q.id]?.picked ?? ''}
            custom={state[q.id]?.custom ?? ''}
            onPick={(v) => setPicked(q.id, v)}
            onCustomChange={(v) => setCustom(q.id, v)}
          />
        ))}
      </div>

      {(answerMut.error || cancelMut.error) && (
        <div className="mt-3 text-sm text-red-700">
          {((answerMut.error || cancelMut.error) as Error).message}
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={answerMut.isPending || cancelMut.isPending}
          className="px-3 py-1.5 text-sm rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {cancelMut.isPending ? 'Cancelling…' : 'Cancel task'}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={answerMut.isPending || cancelMut.isPending}
          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {answerMut.isPending ? 'Submitting…' : 'Submit answers & continue'}
        </button>
      </div>
    </div>
  );
}

function QuestionBlock({
  index,
  question,
  picked,
  custom,
  onPick,
  onCustomChange,
}: {
  index: number;
  question: ClarifyingQuestion;
  picked: string;
  custom: string;
  onPick: (v: string) => void;
  onCustomChange: (v: string) => void;
}) {
  const hasCustom = custom.trim().length > 0;
  return (
    <div className="bg-white border border-orange-200 rounded p-3">
      <div className="font-medium text-sm text-slate-900 mb-1">
        Q{index}: {question.question}
      </div>
      {question.why && (
        <p className="text-xs text-slate-600 mb-2">{question.why}</p>
      )}

      <div className="space-y-1.5 mb-2">
        {question.options.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-start gap-2 text-sm cursor-pointer p-1.5 rounded ${
              picked === opt.value && !hasCustom ? 'bg-emerald-50' : 'hover:bg-slate-50'
            } ${hasCustom ? 'opacity-50' : ''}`}
          >
            <input
              type="radio"
              name={`q-${question.id}`}
              value={opt.value}
              checked={picked === opt.value}
              onChange={(e) => onPick(e.target.value)}
              disabled={hasCustom}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className={picked === opt.value && !hasCustom ? 'font-medium' : ''}>
                {opt.value}
              </span>
              {opt.recommended && (
                <span className="ml-1.5 px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] uppercase">
                  recommended
                </span>
              )}
              {opt.hint && (
                <span className="block text-xs text-slate-600 mt-0.5">
                  {opt.hint}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">
          Or write your own answer (overrides the radio above)
        </label>
        <input
          type="text"
          value={custom}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="Type a custom answer here…"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
    </div>
  );
}
